import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Link2, Loader2, MessageSquare, Plus, Search, Sparkles, Trash2, Upload, Wand2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KbBackButton, KbToolbarButton } from "@/components/KbToolbarButton";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { KbStyleInjector } from "@/lib/kbSettings";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";
import { KB_AUDIENCES } from "@/lib/kbAudiences";

const SOURCES = [
  ["prompt", "From a prompt", Sparkles],
  ["link", "From a link", Link2],
  ["document", "From a document", FileText],
  ["ticket", "From a ticket", MessageSquare],
];

export default function KBContentStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [source, setSource] = useState("prompt");
  const [prompt, setPrompt] = useState("");
  const [audience, setAudience] = useState("internal");
  const [length, setLength] = useState("standard");
  const [doc, setDoc] = useState(null);            // { filename, chars, text }
  const [ticketSource, setTicketSource] = useState("helpdesk");
  const [ticketQuery, setTicketQuery] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketId, setTicketId] = useState("");
  const [links, setLinks] = useState([""]);
  const [fetched, setFetched] = useState(null);      // { sources, failed, chars, images }
  const [withImages, setWithImages] = useState(true);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState(null);
  const [available, setAvailable] = useState(true);
  const embedHosts = useKbEmbedHosts();

  useEffect(() => {
    api.get("/kb/ai/status").then((r) => setAvailable(!!r.data.available)).catch(() => {});
  }, []);

  const loadTickets = useCallback(() => {
    if (source !== "ticket") return;
    setBusy("tickets");
    api.get("/kb/ai/tickets", { params: { q: ticketQuery, source: ticketSource, limit: 25 } })
      .then((r) => setTickets(r.data.tickets || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setBusy(""));
  }, [source, ticketQuery, ticketSource]);

  useEffect(() => { const t = setTimeout(loadTickets, 300); return () => clearTimeout(t); }, [loadTickets]);

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/kb/ai/extract", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(data);
      toast.success(`Read ${data.chars.toLocaleString()} characters from ${data.filename}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't read that file");
    } finally { setBusy(""); }
  };

  const fetchLinks = async () => {
    const urls = links.map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return;
    setBusy("links");
    setFetched(null);
    try {
      const { data } = await api.post("/kb/ai/fetch-url", { urls, with_images: withImages });
      setFetched(data);
      const imgs = data.images ? ` · ${data.images} image${data.images === 1 ? "" : "s"} saved` : "";
      toast.success(`Read ${data.chars.toLocaleString()} characters from ${data.sources.length} page${data.sources.length === 1 ? "" : "s"}${imgs}`);
      (data.failed || []).forEach((f) => toast.warning(`${f.url} — ${f.reason}`));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't read that link");
    } finally { setBusy(""); }
  };

  const generate = async () => {
    setBusy("draft");
    setDraft(null);
    try {
      const { data } = await api.post("/kb/ai/draft", {
        source, prompt, audience, length,
        text: doc?.text || "", ticket_id: ticketId, ticket_source: ticketSource,
        links: (fetched?.sources || []).map((s) => ({
          url: s.url, title: s.title, text: s.text, images: s.images,
        })),
      });
      setDraft(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The assistant is unavailable");
    } finally { setBusy(""); }
  };

  const [reviewer, setReviewer] = useState("");
  const createDraft = async () => {
    if (!reviewer && !isAdmin) { toast.error("Pick a reviewer — every article needs one"); return; }
    setBusy("create");
    try {
      const { data } = await api.post("/kb/articles", {
        name: draft.title || "Untitled draft",
        description: draft.description || "",
        body_html: draft.body_html || "",
        keywords: draft.keywords || [],
        audiences: [audience],
        status: "draft",
        reviewer_id: reviewer || undefined,
        primary_category_id: draft.category_id || null,
      });
      if ((draft.tags || []).length || draft.category_id) {
        try {
          await api.patch(`/kb/articles/${data.slug}`, {
            tags: draft.tags || [],
            revision_note: `AI draft from ${draft.source}${(draft.source_urls || []).length ? `: ${draft.source_urls.join(", ")}` : ""}`,
            ...(draft.category_id ? { primary_category_id: draft.category_id, category_ids: [draft.category_id] } : {}),
          });
        } catch {
          toast.warning("Draft created, but the tags/category didn't stick — set them in the editor");
        }
      }
      toast.success("Draft created — review it before publishing");
      navigate(`/kb/a/${data.slug}/edit`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const canGenerate = available && !busy && (
    (source === "prompt" && prompt.trim().length > 7)
    || (source === "link" && !!fetched?.sources?.length)
    || (source === "document" && !!doc)
    || (source === "ticket" && !!ticketId));

  return (
    <div data-testid="kb-content-studio">
      <KbStyleInjector />
      <PageHeader title="Compose" testid="kb-studio-header"
        subtitle="Turn a prompt, a link, a document or a solved ticket into a first draft — then review it like any other article">
        <KbBackButton />
        <KbToolbarButton icon={FileText} testid="kb-studio-manage"
          onClick={() => navigate("/kb/manage/articles")}>Articles</KbToolbarButton>
      </PageHeader>

      {!available && (
        <Card className="mb-4 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="kb-studio-unavailable">
          Claude isn&apos;t configured on this server, so drafting is unavailable.
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        <Card className="space-y-4 border-slate-200 dark:border-slate-800 p-5">
          <div className="grid grid-cols-2 gap-1.5">
            {SOURCES.map(([v, lbl, Icon]) => (
              <button key={v} type="button" data-testid={`kb-studio-source-${v}`}
                onClick={() => setSource(v)}
                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] font-medium transition-colors ${
                  source === v ? "border-primary bg-primary/5 text-primary"
                               : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <Icon className="h-4 w-4 shrink-0" /> {lbl}
              </button>
            ))}
          </div>

          {source === "prompt" && (
            <div className="space-y-1.5">
              <Label className="text-xs">What should the article cover?</Label>
              <textarea data-testid="kb-studio-prompt" rows={6} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Step-by-step guide for porting a toll-free number to UC, including the documents we need from the customer and typical timelines."
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          )}

          {source === "link" && (
            <div className="space-y-2" data-testid="kb-studio-link-panel">
              <Label className="text-xs">Web pages to turn into an article (up to 3)</Label>
              {links.map((v, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input data-testid={`kb-studio-link-${i}`} value={v}
                    placeholder="https://vendor.example.com/help/setting-up-x"
                    onChange={(e) => setLinks(links.map((l, j) => (j === i ? e.target.value : l)))}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchLinks(); }}
                    className="h-8 text-xs" />
                  {links.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                      data-testid={`kb-studio-link-remove-${i}`}
                      onClick={() => { setLinks(links.filter((_, j) => j !== i)); setFetched(null); }}>
                      <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between">
                {links.length < 3 ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                    data-testid="kb-studio-link-add" onClick={() => setLinks([...links, ""])}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add another link
                  </Button>
                ) : <span className="text-[11px] text-slate-400 dark:text-slate-500">Maximum of 3 links</span>}
                <Button size="sm" className="h-7 px-3 text-xs" data-testid="kb-studio-link-fetch"
                  onClick={fetchLinks} disabled={busy === "links" || !links.some((l) => l.trim())}>
                  {busy === "links" ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Reading…</> : "Read pages"}
                </Button>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                <input type="checkbox" checked={withImages} data-testid="kb-studio-link-images"
                  onChange={(e) => { setWithImages(e.target.checked); setFetched(null); }} />
                Copy the pages&apos; images into our media library and place them in the draft
              </label>
              {!!fetched?.sources?.length && (
                <div className="space-y-1.5 rounded-md border border-slate-100 dark:border-slate-800 p-2"
                  data-testid="kb-studio-link-result">
                  {fetched.sources.map((s) => (
                    <div key={s.url} className="text-[11px]">
                      <span className="block truncate font-medium text-slate-700 dark:text-slate-200">
                        {s.title || s.url}
                      </span>
                      <span className="block truncate text-slate-400 dark:text-slate-500">
                        {s.chars.toLocaleString()} chars · {s.images.length} image{s.images.length === 1 ? "" : "s"} · {s.url}
                      </span>
                    </div>
                  ))}
                  {(fetched.failed || []).map((f) => (
                    <div key={f.url} className="truncate text-[11px] text-amber-600" data-testid="kb-studio-link-failed">
                      {f.url} — {f.reason}
                    </div>
                  ))}
                </div>
              )}
              <textarea data-testid="kb-studio-link-prompt" rows={2} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional: extra instructions (e.g. 'only the admin portal steps')"
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs" />
            </div>
          )}

          {source === "document" && (
            <div className="space-y-2">
              <Label className="text-xs">Source document (PDF, Word, .md, .txt — max 20 MB)</Label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-3 py-6 text-xs text-slate-500 dark:text-slate-400 hover:border-primary/40 hover:bg-primary/5">
                {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {doc ? `${doc.filename} — ${doc.chars.toLocaleString()} chars` : "Choose a file"}
                <input type="file" className="hidden" data-testid="kb-studio-upload"
                  accept=".pdf,.docx,.md,.txt,.markdown"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
              </label>
              <textarea data-testid="kb-studio-doc-prompt" rows={3} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional: extra instructions (e.g. 'focus on the admin steps only')"
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs" />
            </div>
          )}

          {source === "ticket" && (
            <div className="space-y-2">
              <div className="flex gap-1.5">
                {[["helpdesk", "Helpdesk"], ["intercom", "Intercom"]].map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`kb-studio-ticketsrc-${v}`}
                    onClick={() => { setTicketSource(v); setTicketId(""); }}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                      ticketSource === v ? "border-primary bg-primary/10 text-primary"
                                        : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input data-testid="kb-studio-ticket-search" value={ticketQuery}
                  placeholder="Search by subject…" onChange={(e) => setTicketQuery(e.target.value)}
                  className="h-8 pl-8 text-xs" />
              </div>
              <div className="max-h-56 space-y-1 overflow-y-auto">
                {busy === "tickets" && <div className="py-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-300 dark:text-slate-600" /></div>}
                {!busy && tickets.length === 0 && <p className="py-4 text-center text-xs text-slate-400 dark:text-slate-500">No tickets found.</p>}
                {tickets.map((t) => (
                  <button key={t.id} type="button" data-testid={`kb-studio-ticket-${t.id}`}
                    onClick={() => setTicketId(t.id)}
                    className={`w-full rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ${
                      ticketId === t.id ? "border-primary bg-primary/5" : "border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    <span className="block truncate font-medium text-slate-700 dark:text-slate-200">{t.subject}</span>
                    <span className="block truncate text-[10px] text-slate-400 dark:text-slate-500">
                      {t.state}{t.category ? ` · ${t.category}` : ""}{t.at ? ` · ${new Date(t.at).toLocaleDateString()}` : ""}
                    </span>
                  </button>
                ))}
              </div>
              <textarea data-testid="kb-studio-ticket-prompt" rows={2} value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional: extra instructions"
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="h-8 text-xs" data-testid="kb-studio-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KB_AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value} className="text-xs">{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Length</Label>
              <Select value={length} onValueChange={setLength}>
                <SelectTrigger className="h-8 text-xs" data-testid="kb-studio-length"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brief" className="text-xs">Brief</SelectItem>
                  <SelectItem value="standard" className="text-xs">Standard</SelectItem>
                  <SelectItem value="detailed" className="text-xs">Detailed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button className="w-full" data-testid="kb-studio-generate" disabled={!canGenerate} onClick={generate}>
            {busy === "draft" ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Drafting…</>
              : <><Wand2 className="mr-1 h-4 w-4" /> Generate draft</>}
          </Button>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            Drafts are never published automatically — you review, edit and publish them yourself.
          </p>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800 p-6" data-testid="kb-studio-result">
          {!draft ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-sm text-slate-400 dark:text-slate-500">
              <Sparkles className="mb-2 h-6 w-6 text-slate-300 dark:text-slate-600" />
              Your draft will appear here for review.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="kb-studio-draft-title">{draft.title}</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{draft.description}</p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                {draft.category_name && (
                  <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-600 dark:text-slate-300">{draft.category_name}</span>
                )}
                {(draft.tags || []).map((t) => (
                  <span key={t} className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{t}</span>
                ))}
                {(draft.keywords || []).map((k) => (
                  <span key={k} className="rounded-full bg-slate-50 dark:bg-slate-900/60 px-2 py-0.5 text-slate-500 dark:text-slate-400">{k}</span>
                ))}
              </div>
              {!!(draft.open_questions || []).length && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2" data-testid="kb-studio-questions">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Confirm before publishing</div>
                  <ul className="mt-1 space-y-0.5">
                    {draft.open_questions.map((q, i) => (
                      <li key={i} className="text-xs text-amber-800">· {q}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="kb-prose max-h-[52vh] overflow-y-auto rounded-md border border-slate-100 dark:border-slate-800 p-4"
                dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(draft.body_html || "", embedHosts) }} />
              <div className="flex flex-wrap items-center gap-2">
                <KbReviewerSelect compact value={reviewer} onChange={setReviewer} testid="kb-studio-reviewer" />
                <Button data-testid="kb-studio-create" onClick={createDraft} disabled={busy === "create" || (!reviewer && !isAdmin)}>
                  {busy === "create" ? "Creating…" : "Create draft & open editor"}
                </Button>
                <Button variant="outline" data-testid="kb-studio-regenerate" onClick={generate} disabled={!!busy}>
                  Regenerate
                </Button>
                <Button variant="ghost" data-testid="kb-studio-discard" onClick={() => setDraft(null)}>Discard</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
